import React, { useState, useEffect, useCallback, useRef } from 'react'
import { t, jt, c } from 'ttag'
import YumTextField from '../../components/YumTextField/YumTextField'
import YumButton from '../../components/YumButton/YumButton'
import { Card } from '../../components/Card/Card'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import {
  PasswordPolicy,
  SignUp$Request,
  UserProfileDto,
  GetCompanyDetails$Response,
  LoginMethod,
} from '../../services/api.types'
import { Link as RouterLink } from 'react-router-dom'
import Link from '@material-ui/core/Link'
import { ROUTES } from '../../common/Routes/routePaths'
import './SignUp.scss'
import { currentLang, isRtl } from '../../i18nInit'
import {
  isTextFieldErrorVisible,
  isPasswordFieldErrorVisible,
  isEmailFieldErrorVisible,
  isConsentFieldErrorVisible,
} from '../../common/helpers/formHelpers'
import InputAdornment from '@material-ui/core/InputAdornment'
import IconButton from '@material-ui/core/IconButton'
import { ReactComponent as StarIcon } from '../../assets/v2/icons/star.svg'
import { ReactComponent as StarFilledIcon } from '../../assets/v2/icons/star-filled.svg'
import { ReactComponent as StarErrorIcon } from '../../assets/v2/icons/star-error.svg'
import { ReactComponent as EmailIcon } from '../../assets/v2/icons/email.svg'
import { ReactComponent as EmailFilledIcon } from '../../assets/v2/icons/email-filled.svg'
import { ReactComponent as EmailErrorIcon } from '../../assets/v2/icons/email-error.svg'
import { ReactComponent as LockIcon } from '../../assets/v2/icons/lock.svg'
import { ReactComponent as LockFilledIcon } from '../../assets/v2/icons/lock-filled.svg'
import { ReactComponent as LockErrorIcon } from '../../assets/v2/icons/lock-error.svg'
import { ReactComponent as SsoIcon } from '../../assets/v2/icons/sso.svg'
import { ReactComponent as EditIcon } from '../../assets/icons/pencil.svg'
import { red } from '../../styles/material-ui-theme-v2'
import Typography from '@material-ui/core/Typography'
import YumBack from '../../components/YumBack/YumBack'
import { Checkbox, FormControlLabel, FormGroup } from '@material-ui/core'
import YumGoogleSignInWrapper from '../../components/YumGoogleSignIn/YumGoogleSignInWrapper'
import Loader from '../../components/Loader/Loader'
import YumAppleSignInWrapper from '../../components/YumAppleSignIn/YumAppleSignInWrapper'
import { openPricing, openPrivacyPolicy, openTermsOfService, openUrl } from '../../common/helpers/routeHelpers'
import { isNative } from '../../common/helpers/isNative'
import { WelcomeSidebar } from '../Welcome/Welcome'
import YumPasswordPolicy from '../../components/YumPasswordPolicy/YumPasswordPolicy'
import api from '../../services/api.services'
import { apiDomain } from '../../services/apiUrl'
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage'

// --- Types (kept exported for the wrapper) ---

export interface IStateProps {
  authenticated: boolean
  resetPasswordInProgress: boolean
  userDto: UserProfileDto
  lastUpdate: number | null
}

export interface IDispatchProps {
  doSignUp(payload: SignUp$Request): void
  reLocate(): void
}

type ISignUpProps = IStateProps & IDispatchProps

// Error state for each form field
interface FieldErrors {
  companyCode: boolean
  email: boolean
  password: boolean
  consent: boolean
  customPolicyConsent: boolean
}

// Focus state for fields with start adornment icons
interface FieldFocus {
  companyCode: boolean
  email: boolean
  password: boolean
}

// Company details returned by the API (password policy + SSO config)
interface CompanyDetails {
  passwordPolicy: PasswordPolicy
  passwordMinimumLength: number
  customPolicyUrl: string
  customPolicyTitle: string
  ssoEnabled: boolean
  ssoRequired: boolean
  companyId: string | null
  ssoConfigId: string | null
}

const INITIAL_ERRORS: FieldErrors = {
  companyCode: false,
  email: false,
  password: false,
  consent: false,
  customPolicyConsent: false,
}

const INITIAL_FOCUS: FieldFocus = {
  companyCode: false,
  email: false,
  password: false,
}

const INITIAL_COMPANY: CompanyDetails = {
  passwordPolicy: PasswordPolicy.DEFAULT,
  passwordMinimumLength: 6,
  customPolicyUrl: '',
  customPolicyTitle: '',
  ssoEnabled: false,
  ssoRequired: false,
  companyId: null,
  ssoConfigId: null,
}

const NCR_COMPANY_ID = 'eb4b11c3-1321-4c90-86fa-93c595e19171'
const NCR_EMAIL_REGEX = /^[a-zA-Z]{2}\d{6}@ncratleos\.com$/i

// --- Component ---

export const SignUp: React.FC<ISignUpProps> = ({ authenticated, lastUpdate, doSignUp, reLocate }) => {
  // Form field values
  const [companyCode, setCompanyCode] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [consent, setConsent] = useState(false)
  const [customPolicyConsent, setCustomPolicyConsent] = useState(false)

  // Validation & UI state
  const [errors, setErrors] = useState<FieldErrors>(INITIAL_ERRORS)
  const [focus, setFocus] = useState<FieldFocus>(INITIAL_FOCUS)
  const [isFormSubmitted, setIsFormSubmitted] = useState(false)
  const [isPasswordPolicyValid, setIsPasswordPolicyValid] = useState(true)
  const [isSubmitInProgress, setIsSubmitInProgress] = useState(false)

  // Two-step flow: 'code' = enter sign-up code, 'credentials' = email/password or SSO
  const [step, setStep] = useState<'code' | 'credentials'>('code')
  const [isLoadingCompanyDetails, setIsLoadingCompanyDetails] = useState(false)
  const [invalidCode, setInvalidCode] = useState(false)

  // When SSO is enabled but not required, user can toggle to the password form
  const [showPasswordForm, setShowPasswordForm] = useState(false)

  // SSO sub-flow on step 1: lets users without a sign-up code log in via their org's SSO.
  // 'hidden' = normal step 1; 'email' = SSO email input shown instead of step-1 form.
  const [ssoFlowStep, setSsoFlowStep] = useState<'hidden' | 'email'>('hidden')
  const [ssoEmail, setSsoEmail] = useState('')
  const [ssoEmailError, setSsoEmailError] = useState(false)
  const [ssoLookupError, setSsoLookupError] = useState<React.ReactNode | null>(null)
  const [isCheckingSso, setIsCheckingSso] = useState(false)

  // Company details from getCompanyDetails response
  const [company, setCompany] = useState<CompanyDetails>(INITIAL_COMPANY)

  // Ref to track the latest errors for use inside async handlers
  const errorsRef = useRef(errors)
  errorsRef.current = errors

  // Ref to focus the email input when advancing to the credentials step
  const emailInputRef = useRef<HTMLInputElement>(null)

  // --- Lifecycle ---

  // Redirect if already authenticated
  useEffect(() => {
    reLocate()
  }, [])

  useEffect(() => {
    if (authenticated) {
      reLocate()
    }
  }, [authenticated, reLocate])

  // Clear isSubmitInProgress when the saga finishes (lastUpdate changes)
  useEffect(() => {
    setIsSubmitInProgress(false)
  }, [lastUpdate])

  // Focus the email input when advancing to the credentials step
  useEffect(() => {
    if (step === 'credentials' && emailInputRef.current) {
      emailInputRef.current.focus()
    }
  }, [step])

  // --- Field validation ---

  /** Validate a single field and return the error flag. */
  const validateField = useCallback(
    (name: string, value: string | boolean): boolean => {
      let error = false
      switch (name) {
        case 'companyCode':
          error = isTextFieldErrorVisible(value as string)
          break
        case 'email':
          error = isEmailFieldErrorVisible(value as string)
          if (!error && company.companyId === NCR_COMPANY_ID) {
            error = !NCR_EMAIL_REGEX.test(value as string)
          }
          break
        case 'password':
          error = isPasswordFieldErrorVisible(value as string, false, company.passwordMinimumLength)
          break
        case 'consent':
          error = isConsentFieldErrorVisible(value as any)
          break
        case 'customPolicyConsent':
          error = isConsentFieldErrorVisible(value as any)
          break
      }
      setErrors((prev) => ({ ...prev, [name]: error }))
      return error
    },
    [company.passwordMinimumLength, company.companyId],
  )

  // --- API call ---

  /**
   * Fetch company details (password policy + SSO config) for the given sign-up code.
   * On success, advances the form to the credentials step.
   */
  const getCompanyDetailsAndAdvance = useCallback(async (code: string) => {
    if (!code) return

    setIsLoadingCompanyDetails(true)
    try {
      const response = await api.auth.getCompanyDetails({ signUpCode: code })
      const data = response as unknown as GetCompanyDetails$Response

      const {
        passwordPolicy: policyString,
        passwordMinLength,
        customPolicyUrl,
        customPolicyTitle,
        ssoEnabled,
        ssoRequired,
        companyId,
        ssoConfigId,
      } = (data || {}) as any

      // If companyId is missing, the sign-up code is invalid
      if (!companyId) {
        setIsLoadingCompanyDetails(false)
        setInvalidCode(true)
        setErrors((prev) => ({ ...prev, companyCode: true }))
        return
      }

      const passwordPolicy = (policyString || PasswordPolicy.DEFAULT) as PasswordPolicy
      setCompany({
        passwordPolicy,
        passwordMinimumLength: passwordMinLength,
        customPolicyUrl: customPolicyUrl || '',
        customPolicyTitle: customPolicyTitle || '',
        ssoEnabled: !!ssoEnabled,
        ssoRequired: !!ssoRequired,
        companyId: companyId || null,
        ssoConfigId: ssoConfigId || null,
      })
      if (passwordPolicy === PasswordPolicy.DEFAULT) {
        setIsPasswordPolicyValid(true)
      }
      setIsLoadingCompanyDetails(false)
      setInvalidCode(false)
      setIsFormSubmitted(false)
      setStep('credentials')
    } catch (error) {
      console.error('Error fetching company details:', error)
      setIsLoadingCompanyDetails(false)
      setInvalidCode(true)
      setErrors((prev) => ({ ...prev, companyCode: true }))
    }
  }, [])

  // --- Event handlers ---

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value, name } = event.target
      switch (name) {
        case 'companyCode':
          setCompanyCode(value)
          setInvalidCode(false)
          break
        case 'email':
          setEmail(value)
          break
        case 'password':
          setPassword(value)
          break
      }
      validateField(name, value)
    },
    [validateField],
  )

  const handleFocus = useCallback((name: string) => {
    setFocus((prev) => ({ ...prev, [name]: true }))
  }, [])

  const handleBlur = useCallback((name: string) => {
    setFocus((prev) => ({ ...prev, [name]: false }))
  }, [])

  /** Go back to the code step. */
  const handleBackToCode = useCallback(() => {
    setStep('code')
    setEmail('')
    setPassword('')
    setConsent(false)
    setCustomPolicyConsent(false)
    setIsFormSubmitted(false)
    setInvalidCode(false)
    setShowPasswordForm(false)
    setCompany(INITIAL_COMPANY)
    setErrors(INITIAL_ERRORS)
  }, [])

  const handleConsentChange = useCallback(() => {
    setConsent((prev) => {
      const next = !prev
      validateField('consent', next as any)
      return next
    })
  }, [validateField])

  const handleCustomPolicyConsentChange = useCallback(() => {
    setCustomPolicyConsent((prev) => {
      const next = !prev
      validateField('customPolicyConsent', next as any)
      return next
    })
  }, [validateField])

  const handleTermsOfService = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    openTermsOfService()
  }, [])

  const handlePrivacyPolicy = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    openPrivacyPolicy()
  }, [])

  const handleCustomPolicy = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      openUrl(company.customPolicyUrl, true)
    },
    [company.customPolicyUrl],
  )

  /**
   * Redirect to the SSO IdP login page via Spring Security's SAML endpoint.
   * Validates consent checkboxes before redirecting.
   */
  const handleSsoRedirect = useCallback(() => {
    // Validate consent before redirecting
    const consentError = validateField('consent', consent as any)
    if (consentError) {
      setIsFormSubmitted(true)
      return
    }
    if (company.customPolicyUrl) {
      const cpError = validateField('customPolicyConsent', customPolicyConsent as any)
      if (cpError) {
        setIsFormSubmitted(true)
        return
      }
    }

    const redirectId = company.ssoConfigId || company.companyId
    if (redirectId) {
      const ssoUrl = `${apiDomain}/saml2/authenticate/${redirectId}${isNative() ? '?native=true' : ''}`
      openUrl(ssoUrl)
    }
  }, [consent, customPolicyConsent, company, validateField])

  /**
   * Look up the user's SSO config by email (step-1 SSO entry point).
   * On match, redirects to the IdP. On non-SSO email or unknown email, shows an inline error.
   */
  const handleSsoLookup = useCallback(async () => {
    if (isCheckingSso) return
    const emailError = isEmailFieldErrorVisible(ssoEmail)
    setSsoEmailError(emailError)
    if (emailError) return

    setSsoLookupError(null)
    setIsCheckingSso(true)
    try {
      const response: any = await api.auth.checkLoginMethod({ email: ssoEmail })
      const { loginMethod, companyId, ssoConfigId } = response || {}

      if (loginMethod === LoginMethod.SSO_REQUIRED || loginMethod === LoginMethod.SSO_OR_PASSWORD) {
        const redirectId = ssoConfigId || companyId
        if (redirectId) {
          const params = new URLSearchParams()
          params.set('login_hint', ssoEmail)
          if (isNative()) params.set('native', 'true')
          const ssoUrl = `${apiDomain}/saml2/authenticate/${redirectId}?${params.toString()}`
          openUrl(ssoUrl)
          // Keep the button disabled with loader -- openUrl on native doesn't navigate
          // the SPA, so the user could otherwise click again while the IdP page loads.
          return
        }
        setSsoLookupError(t`Could not start SSO sign-in. Please contact your administrator.`)
      } else if (loginMethod === LoginMethod.PASSWORD) {
        const loginLink = <Link component={RouterLink} to={ROUTES.LOGIN.to}>{t`log in`}</Link>
        setSsoLookupError(jt`This account doesn't use SSO. Please ${loginLink} with your password instead.`)
      } else {
        setSsoLookupError(t`This email address isn't enabled for SSO. Please sign up using your company code instead.`)
      }
      setIsCheckingSso(false)
    } catch (error) {
      setSsoLookupError(t`Something went wrong. Please try again.`)
      setIsCheckingSso(false)
    }
  }, [ssoEmail, isCheckingSso])

  /** Form submit handler. */
  const onSubmit = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>) => {
      if (e) e.preventDefault()

      // SSO sub-flow on step 1: look up the user's SSO config by email
      if (ssoFlowStep === 'email') {
        await handleSsoLookup()
        return
      }

      // Step 1: validate company code and fetch company details
      if (step === 'code') {
        setIsFormSubmitted(true)
        const codeError = validateField('companyCode', companyCode)
        if (!codeError) {
          await getCompanyDetailsAndAdvance(companyCode)
        }
        return
      }

      // Step 2 SSO view: no password form to submit
      if (company.ssoEnabled && !showPasswordForm) {
        return
      }

      // Step 2: validate credentials and submit
      setIsFormSubmitted(true)
      const codeErr = validateField('companyCode', companyCode)
      const emailErr = validateField('email', email)
      const passwordErr = validateField('password', password)
      const consentErr = validateField('consent', consent as any)
      let cpErr = false
      if (company.customPolicyUrl) {
        cpErr = validateField('customPolicyConsent', customPolicyConsent as any)
      }

      const hasErrors = codeErr || emailErr || passwordErr || consentErr || cpErr || !isPasswordPolicyValid
      if (!hasErrors) {
        setIsSubmitInProgress(true)
        doSignUp({ companyCode, email, password, lang: currentLang } as SignUp$Request)
      }
    },
    [
      step,
      ssoFlowStep,
      handleSsoLookup,
      companyCode,
      email,
      password,
      consent,
      customPolicyConsent,
      company,
      showPasswordForm,
      isPasswordPolicyValid,
      validateField,
      getCompanyDetailsAndAdvance,
      doSignUp,
    ],
  )

  /** Submit on Enter key. */
  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.keyCode === 13) {
        event.preventDefault()
        ;(event.target as HTMLInputElement).blur()
        onSubmit()
      }
    },
    [onSubmit],
  )

  // --- Derived values ---

  const companyCodeNotEmpty = !!companyCode
  const emailNotEmpty = !!email
  const passwordNotEmpty = !!password
  const isOnCredentialsStep = step === 'credentials'

  // --- Consent label links ---

  const tos = (
    <a key="tos" className="link-blue" onClick={handleTermsOfService}>
      {c('In sentence').t`Terms of Service`}
    </a>
  )
  const pp = (
    <a key="pp" className="link-blue" onClick={handlePrivacyPolicy}>
      {c('In sentence').t`Privacy Policy`}
    </a>
  )
  const cp = (
    <a key="cp" className="link-blue" onClick={handleCustomPolicy}>
      {company.customPolicyTitle || t`My organization's policy`}
    </a>
  )

  // Company code end adornment: only the edit icon on step 2
  let companyCodeEndAdornment: React.ReactNode = ''
  if (isOnCredentialsStep) {
    companyCodeEndAdornment = (
      <InputAdornment position="end">
        <IconButton aria-label="edit sign-up code" onClick={handleBackToCode} edge="end">
          <EditIcon width={24} height={24} />
        </IconButton>
      </InputAdornment>
    )
  }

  // --- Shared consent checkboxes (used in both SSO and password views) ---

  const renderConsentCheckboxes = () => (
    <>
      <FormGroup row={true} style={{ marginTop: 16 }}>
        <FormControlLabel
          control={
            <Checkbox color="primary" required={true} name="consent" checked={consent} onChange={handleConsentChange} />
          }
          label={<Typography style={{ fontSize: 14 }}>{jt`I agree to YuMuuv's ${tos} and ${pp}.`}</Typography>}
          //style={errors.consent ? { color: red } : {}}
        />
      </FormGroup>
      {isFormSubmitted && errors.consent && <ErrorMessage>{t`You must agree to YuMuuv policies.`}</ErrorMessage>}
      {company.customPolicyUrl && (
        <FormGroup row={true}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                required={true}
                name="customPolicyConsent"
                checked={customPolicyConsent}
                onChange={handleCustomPolicyConsentChange}
              />
            }
            label={<Typography style={{ fontSize: 14 }}>{jt`I agree to: ${cp}`}</Typography>}
            //style={errors.customPolicyConsent ? { color: red } : {}}
          />
        </FormGroup>
      )}
      {isFormSubmitted && errors.customPolicyConsent && (
        <ErrorMessage>{t`You must agree to your organization's policy.`}</ErrorMessage>
      )}
    </>
  )

  // --- Render ---

  return (
    <Card className="sign-up pt-12">
      <div className="sign-up__inner">
        <YumBack to="/" />
        <NotificationsWrapper />
        <Loader />

        <form className="sign-up__form" onSubmit={onSubmit} noValidate>
          <Typography className="h1-desktop" variant="h1">
            {t`Join YuMuuv`}
          </Typography>
          <div className="info">
            <span>{t`You can only join YuMuuv as part of an organization that has purchased a plan.`}</span>{' '}
            <a href={undefined} onClick={openPricing}>{t`Learn more`}</a>
          </div>

          {/* Company code field -- hidden during SSO sub-flow, disabled on step 2 */}
          {ssoFlowStep === 'hidden' && (
            <>
              <YumTextField
                placeholder={t`Sign-up code`}
                type="text"
                name="companyCode"
                style={{ marginBottom: 0 }}
                className="uppercase-value"
                value={companyCode}
                error={errors.companyCode}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                onFocus={() => handleFocus('companyCode')}
                onBlur={() => handleBlur('companyCode')}
                autoCapitalize="characters"
                disabled={isOnCredentialsStep}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {errors.companyCode && !focus.companyCode ? (
                        <StarErrorIcon />
                      ) : companyCodeNotEmpty || focus.companyCode ? (
                        <StarFilledIcon />
                      ) : (
                        <StarIcon />
                      )}
                    </InputAdornment>
                  ),
                  endAdornment: companyCodeEndAdornment,
                }}
              />

              {/* Sign-up code error messages */}
              {errors.companyCode && (
                <ErrorMessage>
                  {invalidCode
                    ? t`Double check the sign-up code or contact the organizer for help.`
                    : t`Please enter your sign-up code.`}
                </ErrorMessage>
              )}
            </>
          )}

          {/* Step 1: company code + Continue button + SSO/Apple/Google */}
          {!isOnCredentialsStep && ssoFlowStep === 'hidden' && (
            <>
              <YumButton
                style={{ marginTop: 16 }}
                type="submit"
                showLoader={true}
                disabled={isLoadingCompanyDetails}
              >{t`Continue`}</YumButton>
              <div className="text-center">
                <div className="or-line">
                  <span className="or-line-inner">{t`or`}</span>
                </div>
                <YumButton
                  className="btn-sign-up-with-sso"
                  variant="outlined"
                  disabled={isLoadingCompanyDetails}
                  onClick={() => {
                    setSsoFlowStep('email')
                    setSsoLookupError(null)
                    setSsoEmailError(false)
                  }}
                >
                  <SsoIcon style={{ marginTop: -4, marginRight: isRtl ? 0 : 10, marginLeft: isRtl ? 10 : 0 }} />
                  {t`Sign up with SSO`}
                </YumButton>
                <YumAppleSignInWrapper
                  buttonText={t`Sign up with Apple`}
                  isSignUp={true}
                  disabled={isLoadingCompanyDetails}
                />
                <YumGoogleSignInWrapper
                  buttonText={t`Sign up with Google`}
                  isSignUp={true}
                  disabled={isLoadingCompanyDetails}
                />
              </div>
            </>
          )}

          {/* SSO sub-flow on step 1: email lookup -> redirect to IdP */}
          {ssoFlowStep === 'email' && (
            <>
              <YumTextField
                style={{ marginTop: 0, marginBottom: 0 }}
                placeholder={t`Email`}
                type="email"
                name="ssoEmail"
                autoComplete="email"
                value={ssoEmail}
                error={ssoEmailError}
                onChange={(e) => {
                  setSsoEmail(e.target.value)
                  if (ssoEmailError) setSsoEmailError(false)
                  if (ssoLookupError) setSsoLookupError(null)
                }}
                onKeyDown={(e) => {
                  if (e.keyCode === 13) {
                    e.preventDefault()
                    ;(e.target as HTMLInputElement).blur()
                    handleSsoLookup()
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {ssoEmailError ? <EmailErrorIcon /> : ssoEmail ? <EmailFilledIcon /> : <EmailIcon />}
                    </InputAdornment>
                  ),
                }}
              />
              {ssoEmailError && <ErrorMessage>{t`Please enter a valid email address.`}</ErrorMessage>}
              {ssoLookupError && <ErrorMessage>{ssoLookupError}</ErrorMessage>}
              <YumButton
                style={{ marginTop: 16 }}
                type="submit"
                showLoader={true}
                disabled={isCheckingSso}
              >{t`Continue with SSO`}</YumButton>
              <div className="text-center" style={{ marginTop: 16 }}>
                <a
                  className="link-blue"
                  style={{ cursor: 'pointer', fontSize: 14 }}
                  onClick={() => {
                    setSsoFlowStep('hidden')
                    setSsoLookupError(null)
                    setSsoEmailError(false)
                  }}
                >{t`Back`}</a>
              </div>
            </>
          )}

          {/* Step 2: credentials form -- SSO-only, SSO default, or password */}
          {isOnCredentialsStep && (
            <>
              {/* SSO view: shown when ssoEnabled and user hasn't toggled to password */}
              {company.ssoEnabled && !showPasswordForm ? (
                <>
                  {company.ssoRequired && (
                    <div className="info" style={{ marginTop: 8 }}>
                      <span>{t`Your organization requires sign-up via SSO.`}</span>
                    </div>
                  )}
                  {renderConsentCheckboxes()}
                  <YumButton style={{ marginTop: 16 }} onClick={handleSsoRedirect}>{t`Sign up with SSO`}</YumButton>
                  {/* If SSO is not required, allow switching to password sign-up */}
                  {!company.ssoRequired && (
                    <div className="text-center" style={{ marginTop: 16 }}>
                      <a
                        className="link-blue"
                        style={{ cursor: 'pointer', fontSize: 14 }}
                        onClick={() => setShowPasswordForm(true)}
                      >{t`Sign up with password instead`}</a>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Password flow: email + password fields */}
                  <YumTextField
                    style={{ marginTop: 16, marginBottom: 0 }}
                    placeholder={t`Email`}
                    type="email"
                    name="email"
                    autoComplete="email"
                    inputRef={emailInputRef}
                    error={isFormSubmitted && errors.email}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyPress}
                    onFocus={() => handleFocus('email')}
                    onBlur={() => handleBlur('email')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {isFormSubmitted && errors.email && !focus.email ? (
                            <EmailErrorIcon />
                          ) : emailNotEmpty || focus.email ? (
                            <EmailFilledIcon />
                          ) : (
                            <EmailIcon />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                  {isFormSubmitted && errors.email && (
                    <ErrorMessage>
                      {!email
                        ? t`Please enter an email address.`
                        : company.companyId === NCR_COMPANY_ID && !NCR_EMAIL_REGEX.test(email)
                          ? t`Use your [QUICKLOOKID]@ncratleos.com email.`
                          : t`Email is not valid.`}
                    </ErrorMessage>
                  )}
                  <YumTextField
                    style={{ marginTop: 16, marginBottom: 0 }}
                    placeholder={t`Password`}
                    type="password"
                    name="password"
                    error={isFormSubmitted && (errors.password || !isPasswordPolicyValid)}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyPress}
                    onFocus={() => handleFocus('password')}
                    onBlur={() => handleBlur('password')}
                    autoComplete="new-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {isFormSubmitted && errors.password && !focus.password ? (
                            <LockErrorIcon />
                          ) : passwordNotEmpty || focus.password ? (
                            <LockFilledIcon />
                          ) : (
                            <LockIcon />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                  {isFormSubmitted && errors.password && (
                    <ErrorMessage>
                      {t`Password must be at least 6 characters.`.replace('6', String(company.passwordMinimumLength))}
                    </ErrorMessage>
                  )}
                  {company.passwordPolicy !== PasswordPolicy.DEFAULT && (
                    <div style={{ marginTop: 12 }}>
                      <YumPasswordPolicy
                        policy={company.passwordPolicy}
                        password={password}
                        onPolicyChange={setIsPasswordPolicyValid}
                      />
                    </div>
                  )}
                  {renderConsentCheckboxes()}
                  <YumButton
                    style={{ marginTop: 16 }}
                    type="submit"
                    showLoader={true}
                    disabled={isSubmitInProgress}
                  >{t`Sign up`}</YumButton>
                  {/* If SSO is enabled, offer to go back to SSO view */}
                  {company.ssoEnabled && (
                    <div className="text-center" style={{ marginTop: 16 }}>
                      <a
                        className="link-blue"
                        style={{ cursor: 'pointer', fontSize: 14 }}
                        onClick={() => setShowPasswordForm(false)}
                      >{t`Sign up with SSO instead`}</a>
                    </div>
                  )}
                  <div className="text-center">
                    <div className="or-line">
                      <span className="or-line-inner">{t`or`}</span>
                    </div>
                    <YumAppleSignInWrapper
                      buttonText={t`Sign up with Apple`}
                      isSignUp={true}
                      disabled={isSubmitInProgress}
                    />
                    <YumGoogleSignInWrapper
                      buttonText={t`Sign up with Google`}
                      isSignUp={true}
                      disabled={isSubmitInProgress}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </form>
      </div>
      <WelcomeSidebar />
    </Card>
  )
}
